import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface UserProfile {
  id: string;
  username: string | null;
  avatar_url: string | null;
}

interface FeaturedVehicle {
  id: string;
  primary_photo_url: string | null;
  nickname: string | null;
}

export const useCurrentUserProfile = () => {
  const { user } = useAuth();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['current-user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('users')
        .select('id, username, avatar_url')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      return data as UserProfile | null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: featuredVehicle, isLoading: vehicleLoading } = useQuery({
    queryKey: ['current-user-featured-vehicle', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('user_vehicles')
        .select('id, primary_photo_url, nickname')
        .eq('user_id', user.id)
        .eq('is_featured', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching featured vehicle:', error);
        return null;
      }

      return data as FeaturedVehicle | null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    profile,
    featuredVehicle,
    isLoading: profileLoading || vehicleLoading,
  };
};
